
import React from "react";
import { Link } from "react-router-dom";
import { 
  ArrowRight, Check, Clock, Bookmark, 
  ThumbsUp, ThumbsDown, MessageCircle, 
  Info, AlertTriangle, Wallet, DollarSign,
  ShoppingCart, CreditCard, Lock
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

interface MarketplaceContentProps {
  activePage: string;
  feedbackSubmitted: Record<string, boolean>;
  bookmarkedArticles: string[];
  submitFeedback: (articleId: string, isHelpful: boolean) => void;
  toggleBookmark: (articleId: string) => void;
}

export const MarketplaceContent: React.FC<MarketplaceContentProps> = ({
  activePage,
  feedbackSubmitted,
  bookmarkedArticles,
  submitFeedback,
  toggleBookmark,
}) => {
  if (activePage === "marketplace-overview") {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Marketplace Overview</h1>
          <p className="text-gray-500 mt-2">
            Learn about the DGK Platform Marketplace, where you can buy and sell professional services.
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            <span>Last updated: June 30, 2023</span>
            <Badge variant="outline" className="ml-2">Core Feature</Badge>
          </div>
        </div>

        <div className="prose max-w-none">
          <h2 id="about-marketplace">About the Marketplace</h2>
          <p>
            The DGK Marketplace connects freelancers and agencies with clients looking for professional services. It features a secure
            transaction system, quality assurance standards, and transparent reviews to create a trusted environment for all participants.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
            <Card className="border-l-4 border-l-emerald-500">
              <CardContent className="pt-6">
                <div className="flex items-start">
                  <ShoppingCart className="h-6 w-6 text-emerald-500 mr-3" />
                  <div>
                    <h3 className="font-medium mb-1">For Buyers</h3>
                    <p className="text-sm text-gray-500">
                      Find quality services with verified reviews, secure payments, and work protection guarantees.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="pt-6">
                <div className="flex items-start">
                  <Wallet className="h-6 w-6 text-blue-500 mr-3" />
                  <div>
                    <h3 className="font-medium mb-1">For Sellers</h3>
                    <p className="text-sm text-gray-500">
                      Offer your services to a global audience with secure payment processing and exposure to qualified clients.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <h2 id="marketplace-categories">Marketplace Categories</h2>
          <p>
            The Marketplace is organized into main categories to help you find or offer specific services:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h3 className="font-medium mb-2">Design & Creative</h3>
              <ul className="space-y-1 text-sm">
                <li className="flex items-center">
                  <Check className="h-4 w-4 text-emerald-500 mr-2" />
                  <span>Logo & Brand Identity</span>
                </li>
                <li className="flex items-center">
                  <Check className="h-4 w-4 text-emerald-500 mr-2" />
                  <span>Web & App Design</span>
                </li>
                <li className="flex items-center">
                  <Check className="h-4 w-4 text-emerald-500 mr-2" />
                  <span>UI/UX Design</span>
                </li>
                <li className="flex items-center">
                  <Check className="h-4 w-4 text-emerald-500 mr-2" />
                  <span>Illustration & Graphics</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h3 className="font-medium mb-2">Development & IT</h3>
              <ul className="space-y-1 text-sm">
                <li className="flex items-center">
                  <Check className="h-4 w-4 text-emerald-500 mr-2" />
                  <span>Web Development</span>
                </li>
                <li className="flex items-center">
                  <Check className="h-4 w-4 text-emerald-500 mr-2" />
                  <span>Mobile App Development</span>
                </li>
                <li className="flex items-center">
                  <Check className="h-4 w-4 text-emerald-500 mr-2" />
                  <span>Software Development</span>
                </li>
                <li className="flex items-center">
                  <Check className="h-4 w-4 text-emerald-500 mr-2" />
                  <span>DevOps & Cloud Services</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h3 className="font-medium mb-2">Marketing & SEO</h3>
              <ul className="space-y-1 text-sm">
                <li className="flex items-center">
                  <Check className="h-4 w-4 text-emerald-500 mr-2" />
                  <span>Digital Marketing</span>
                </li>
                <li className="flex items-center">
                  <Check className="h-4 w-4 text-emerald-500 mr-2" />
                  <span>SEO Services</span>
                </li>
                <li className="flex items-center">
                  <Check className="h-4 w-4 text-emerald-500 mr-2" />
                  <span>Social Media Marketing</span>
                </li>
                <li className="flex items-center">
                  <Check className="h-4 w-4 text-emerald-500 mr-2" />
                  <span>Content Creation</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 my-6">
            <h3 className="text-base font-medium text-blue-800 mb-2 flex items-center">
              <Info className="h-5 w-5 mr-2" />
              Marketplace Quality Standards
            </h3>
            <p className="text-blue-700 text-sm mb-3">
              All marketplace listings are subject to our quality standards to ensure a high-quality experience:
            </p>
            <ul className="text-sm text-blue-700 space-y-1">
              <li className="flex items-center">
                <Check className="h-4 w-4 mr-2 text-blue-600" />
                <span>Clear service descriptions and deliverables</span>
              </li>
              <li className="flex items-center">
                <Check className="h-4 w-4 mr-2 text-blue-600" />
                <span>Accurate turnaround times</span>
              </li>
              <li className="flex items-center">
                <Check className="h-4 w-4 mr-2 text-blue-600" />
                <span>Transparent pricing</span>
              </li>
              <li className="flex items-center">
                <Check className="h-4 w-4 mr-2 text-blue-600" />
                <span>Professional communication</span>
              </li>
              <li className="flex items-center">
                <Check className="h-4 w-4 mr-2 text-blue-600" />
                <span>Verified portfolio items</span>
              </li>
            </ul>
          </div>

          <h2 id="transaction-protection">Transaction Protection</h2>
          <p>
            All marketplace transactions are protected by our secure payment system and escrow service:
          </p>
          
          <Accordion type="single" collapsible className="w-full my-6">
            <AccordionItem value="item-1">
              <AccordionTrigger className="text-base font-medium">
                Escrow Protection
              </AccordionTrigger>
              <AccordionContent>
                <p className="mb-2">
                  Payments are held in escrow until the project is completed and accepted by the client. This protects 
                  both parties and ensures fair transactions.
                </p>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="flex items-center font-medium text-gray-700 mb-2">
                    <Lock className="h-4 w-4 mr-2" /> How it works:
                  </div>
                  <ol className="list-decimal pl-5 text-sm text-gray-600 space-y-1">
                    <li>Client deposits payment to escrow</li>
                    <li>Freelancer completes and delivers work</li>
                    <li>Client reviews and accepts the delivery</li>
                    <li>Payment is released to freelancer</li>
                  </ol>
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger className="text-base font-medium">
                Dispute Resolution
              </AccordionTrigger>
              <AccordionContent>
                <p className="mb-2">
                  If any issues arise during a project, our dispute resolution system helps mediate and find fair solutions.
                </p>
                <div className="flex items-center bg-amber-50 text-amber-800 p-3 rounded-lg">
                  <AlertTriangle className="h-5 w-5 mr-2 flex-shrink-0" />
                  <p className="text-sm">
                    Disputes should be opened within 14 days of delivery to ensure proper investigation and resolution.
                  </p>
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3">
              <AccordionTrigger className="text-base font-medium">
                Payment Methods
              </AccordionTrigger>
              <AccordionContent>
                <p className="mb-2">
                  We support multiple secure payment methods to accommodate users worldwide:
                </p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center">
                    <CreditCard className="h-4 w-4 text-gray-600 mr-2" />
                    <span>Credit/Debit Cards</span>
                  </div>
                  <div className="flex items-center">
                    <DollarSign className="h-4 w-4 text-gray-600 mr-2" />
                    <span>Bank Transfers</span>
                  </div>
                  <div className="flex items-center">
                    <Wallet className="h-4 w-4 text-gray-600 mr-2" />
                    <span>Digital Wallets</span>
                  </div>
                  <div className="flex items-center">
                    <Wallet className="h-4 w-4 text-gray-600 mr-2" />
                    <span>Platform Credits</span>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
        
        {/* Feedback section */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-10">
          <h3 className="font-medium mb-3">Was this article helpful?</h3>
          {!feedbackSubmitted["marketplace-overview"] ? (
            <div className="flex space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("marketplace-overview", true)}
                className="flex items-center"
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it helped
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("marketplace-overview", false)}
                className="flex items-center"
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, I need more info
              </Button>
            </div>
          ) : (
            <div className="text-green-600 flex items-center">
              <Check className="h-5 w-5 mr-2" />
              Thank you for your feedback!
            </div>
          )}
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <MessageCircle className="h-4 w-4 mr-2" />
            <Link to="#" className="text-blue-600 hover:underline">
              Ask a question
            </Link>
            <span className="mx-2">·</span>
            <Bookmark 
              className={`h-4 w-4 mr-2 ${bookmarkedArticles.includes("marketplace-overview") ? "fill-blue-600 text-blue-600" : ""}`} 
            />
            <Button 
              variant="link" 
              className="p-0 h-auto text-sm"
              onClick={() => toggleBookmark("marketplace-overview")}
            >
              {bookmarkedArticles.includes("marketplace-overview") ? "Bookmarked" : "Bookmark"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (activePage === "payments") {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Payments & Transactions</h1>
          <p className="text-gray-500 mt-2">
            Understand how payments work on the DGK platform for safe and secure transactions.
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            <span>Last updated: July 5, 2023</span>
            <Badge variant="outline" className="ml-2">Important</Badge>
          </div>
        </div>

        <div className="prose max-w-none">
          <h2 id="payment-overview">Payment System Overview</h2>
          <p>
            Our payment system is designed to facilitate secure, reliable transactions between buyers and sellers on the platform.
            We use industry-standard security measures and escrow protection to ensure all parties are protected.
          </p>

          <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 my-6">
            <h3 className="font-medium mb-4">Payment Flow</h3>
            <div className="relative">
              {/* Payment flow steps */}
              <div className="hidden md:block absolute left-[18px] top-[40px] bottom-10 w-0.5 bg-gray-300 z-0"></div>
              
              <div className="space-y-8">
                <div className="relative flex items-start z-10">
                  <div className="flex-shrink-0 mr-4">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-medium">
                      1
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium">Client Makes Payment</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Client pays for services using their preferred payment method. The full amount is collected upfront.
                    </p>
                  </div>
                </div>
                
                <div className="relative flex items-start z-10">
                  <div className="flex-shrink-0 mr-4">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-medium">
                      2
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium">Funds Held in Escrow</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      The payment is securely held in escrow while the service provider completes the work.
                    </p>
                  </div>
                </div>
                
                <div className="relative flex items-start z-10">
                  <div className="flex-shrink-0 mr-4">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-medium">
                      3
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium">Work Delivery & Approval</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Service provider delivers the work, and the client reviews it for approval.
                    </p>
                  </div>
                </div>
                
                <div className="relative flex items-start z-10">
                  <div className="flex-shrink-0 mr-4">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-green-100 text-green-600 font-medium">
                      4
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium">Payment Release</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      After client approval, the payment is released to the service provider's account, minus platform fees.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <h2 id="payment-methods">Supported Payment Methods</h2>
          <p>
            We offer a variety of payment methods to accommodate users globally:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-medium mb-3">For Clients</h3>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <CreditCard className="h-4 w-4 text-gray-600 mr-2" />
                    <span>Credit/Debit Cards (Visa, Mastercard, Amex)</span>
                  </li>
                  <li className="flex items-center">
                    <Wallet className="h-4 w-4 text-gray-600 mr-2" />
                    <span>PayPal</span>
                  </li>
                  <li className="flex items-center">
                    <DollarSign className="h-4 w-4 text-gray-600 mr-2" />
                    <span>Bank Transfers (ACH/SEPA)</span>
                  </li>
                  <li className="flex items-center">
                    <Wallet className="h-4 w-4 text-gray-600 mr-2" />
                    <span>Platform Credits</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-medium mb-3">For Service Providers</h3>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <DollarSign className="h-4 w-4 text-gray-600 mr-2" />
                    <span>Direct bank deposits</span>
                  </li>
                  <li className="flex items-center">
                    <Wallet className="h-4 w-4 text-gray-600 mr-2" />
                    <span>PayPal</span>
                  </li>
                  <li className="flex items-center">
                    <CreditCard className="h-4 w-4 text-gray-600 mr-2" />
                    <span>Platform debit card</span>
                  </li>
                  <li className="flex items-center">
                    <Wallet className="h-4 w-4 text-gray-600 mr-2" />
                    <span>Platform wallet (for platform purchases)</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 my-6">
            <h3 className="text-base font-medium text-amber-800 mb-2 flex items-center">
              <AlertTriangle className="h-5 w-5 mr-2" />
              Important Payment Guidelines
            </h3>
            <ul className="text-sm text-amber-700 space-y-2">
              <li className="flex items-start">
                <Check className="h-4 w-4 mt-1 mr-2 flex-shrink-0" />
                <p>Never accept payments outside the platform, as this bypasses our protection systems and violates our Terms of Service.</p>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 mt-1 mr-2 flex-shrink-0" />
                <p>Always create proper contracts or service agreements within the platform before beginning work.</p>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 mt-1 mr-2 flex-shrink-0" />
                <p>For milestone-based projects, break down payments into manageable segments to reduce risk.</p>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Feedback section */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-10">
          <h3 className="font-medium mb-3">Was this article helpful?</h3>
          {!feedbackSubmitted["payments"] ? (
            <div className="flex space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("payments", true)}
                className="flex items-center"
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it helped
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("payments", false)}
                className="flex items-center"
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, I need more info
              </Button>
            </div>
          ) : (
            <div className="text-green-600 flex items-center">
              <Check className="h-5 w-5 mr-2" />
              Thank you for your feedback!
            </div>
          )}
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <MessageCircle className="h-4 w-4 mr-2" />
            <Link to="#" className="text-blue-600 hover:underline">
              Ask a question
            </Link>
            <span className="mx-2">·</span>
            <Bookmark 
              className={`h-4 w-4 mr-2 ${bookmarkedArticles.includes("payments") ? "fill-blue-600 text-blue-600" : ""}`} 
            />
            <Button 
              variant="link" 
              className="p-0 h-auto text-sm"
              onClick={() => toggleBookmark("payments")}
            >
              {bookmarkedArticles.includes("payments") ? "Bookmarked" : "Bookmark"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
